const DEFAULT_MAX_SYMBOLS = 100;

export function canonicalWatchlistKey(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/^\^/, "")
    .replace(/\.(NS|BO|TO|L|AX|DE|F|T)$/i, "");
}

export function normalizeWatchlistSymbol(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9.^=-]/g, "")
    .slice(0, 24);
}

export function uniqueWatchlistSymbols(values, max = DEFAULT_MAX_SYMBOLS) {
  if (!Array.isArray(values)) return [];
  const normalizedMax = Number.isFinite(max) ? Math.max(0, Math.trunc(max)) : DEFAULT_MAX_SYMBOLS;
  if (normalizedMax === 0) return [];
  const seen = new Set();
  const symbols = [];
  for (const value of values) {
    const symbol = normalizeWatchlistSymbol(value);
    const key = canonicalWatchlistKey(symbol) || symbol;
    if (!symbol || seen.has(key)) continue;
    seen.add(key);
    symbols.push(symbol);
    if (symbols.length >= normalizedMax) break;
  }
  return symbols;
}

export function hiddenDefaultWatchlistKeys(values, defaultSymbols) {
  const defaults = new Set(
    uniqueWatchlistSymbols(defaultSymbols).map((symbol) => canonicalWatchlistKey(symbol)),
  );
  const candidates = Array.isArray(values)
    ? values
    : String(values || "")
        .split(",")
        .map((value) => value.trim());
  const hidden = [];
  const seen = new Set();
  for (const value of candidates) {
    const key = canonicalWatchlistKey(value);
    if (!key || !defaults.has(key) || seen.has(key)) continue;
    seen.add(key);
    hidden.push(key);
  }
  return hidden;
}

export function encodeHiddenDefaultWatchlistKeys(values, defaultSymbols) {
  return hiddenDefaultWatchlistKeys(values, defaultSymbols).sort().join(",");
}

/**
 * Android's wire-level `watchlist` document is the explicit extras set only.
 * Country defaults are materialized on each client and removed through the
 * independent `hiddenDefaultStockSymbols` preference.
 */
export function explicitWatchlistExtrasForSync(extraSymbols, max = DEFAULT_MAX_SYMBOLS) {
  return uniqueWatchlistSymbols(extraSymbols, max);
}

export function effectiveWatchlistSymbols({
  defaultSymbols,
  hiddenDefaultSymbols,
  extraSymbols,
  max = DEFAULT_MAX_SYMBOLS,
}) {
  const defaults = uniqueWatchlistSymbols(defaultSymbols, max);
  const hidden = new Set(hiddenDefaultWatchlistKeys(hiddenDefaultSymbols, defaults));
  return uniqueWatchlistSymbols(
    [
      ...defaults.filter((symbol) => !hidden.has(canonicalWatchlistKey(symbol))),
      ...explicitWatchlistExtrasForSync(extraSymbols, max),
    ],
    max,
  );
}
